// @ts-strict-ignore
import { BaseArticleLocationProcessor } from './base-article-location.processor';
import { IArticleLocationProcessor, InsertedArticleData } from './i-article-location.processor';
import { ArticleAccessoryManager } from '@article/manager/article-accessory.manager';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { RelativePositionReferenceArticleLocationVO } from '../model/vo/relative-position-reference-article-location.vo';
import { ArticleUsageVO, ArticleVO } from '@article/model/vo/article.vo';
import { ContainerService } from '@3d-scene/model3d/container.service';
import { Connection } from '@3d-scene/model3d/connection';
import { AdvancedLine } from '@3d-scene/model/advanced-line';
import { Dictionary } from 'typescript-collections';
import { Vector3 } from 'three';
import { CompositeArticleUtils } from '@article/util/composite-article-utils';
import { ArticleContainerUtils } from '@3d-scene/model3d/util/article-container-utils';
import { NumberUtils } from '@shared/util/number-utils';
import { MathUtils } from '@3d-scene/common/util/math-utils';
import { ArticleUsageUtils } from '@article/util/article-usage-utils';
import { ConnectionAreaVO, MetaElementUsageIdentityVO, MetaElementVO } from '@shared/vo/meta-element.vo';
import { ReplaceLabelingArticleHelper } from '../../label/replace-labeling-helper/replace-labeling-article.helper';
import { ReplaceLabelingUtils } from '../../label/replace-labeling-helper/replace-labeling-utils';
import { IOperation } from '@shared/undo/operation/i-operation';
import { InsertArticleHelperFactory } from '@article/helper/insert-article-helper.factory';
import { ProjectHelper } from '@project/helper/project.helper';
import { LocaleResourceManager } from '@shared/manager/locale-resource.manager';
import { ArticleInsertProcessorRegistry } from './prepare/insert/article-insert-processor-registry';
import { ProjectModel } from '@project/model/project.model';
import { ArticleManager } from '@article/manager/article.manager';
import { InsertArticleUtils } from '../util/insert-article-utils';
import { ProjectMetaElementService } from '@project/manager/project-meta-element.service';
import { ConnectionInfoUtils } from '@3d-scene/model3d/util/connection-info-utils';
import { ReplaceBusbarArticleHelper } from '@article/helper/replace/busbar/replace-busbar-article.helper';
import { ConnectionInfoService } from '@3d-scene/model3d/service/connection-info.service';
import { ReplaceBusbarService } from '@article/helper/replace/busbar/replace-busbar.service';
import { AddArticleError, NotEnoughSpaceError } from './add-article-error';
import { CompositeArticleInfoUtils } from '@article/util/composite-article-info-utils';

export class AddArticleToRelativeLocationProcessor extends BaseArticleLocationProcessor<RelativePositionReferenceArticleLocationVO> implements IArticleLocationProcessor {

  private _neighborArticleContainer: ArticleContainer;

  constructor(
    articleLocation: RelativePositionReferenceArticleLocationVO,
    article: ArticleVO,
    insertProcessorRegistry: ArticleInsertProcessorRegistry,
    projectHelper: ProjectHelper,
    localeResourceManager: LocaleResourceManager,
    insertArticleHelperFactory: InsertArticleHelperFactory,
    connectionInfoService: ConnectionInfoService,
    replaceBusbarHelper: ReplaceBusbarArticleHelper,
    projectModel: ProjectModel,
    private projectMetaElementService: ProjectMetaElementService,
    articleManager: ArticleManager,
    private articleAccessoryManager: ArticleAccessoryManager,
    private replaceLabelingHelper: ReplaceLabelingArticleHelper,
    private compositeArticleUtils: CompositeArticleUtils,
    private containerService: ContainerService,
    private replaceLabelingUtils: ReplaceLabelingUtils,
    private insertArticleUtils: InsertArticleUtils,
    private replaceBusbarService: ReplaceBusbarService
  ) {
    super(
      projectModel,
      articleManager,
      insertProcessorRegistry,
      projectHelper,
      localeResourceManager,
      articleLocation,
      article,
      connectionInfoService,
      replaceBusbarHelper,
      insertArticleHelperFactory
    );
  }


  public async process(): Promise<InsertedArticleData> {
    await this.loadArea();
    super.initInsertArticleHelper();
    await super.loadAndProcessFullArticle();
    await super.createAndRefreshWrapper();
    return this.insertArticle();
  }

  private async loadArea(): Promise<void> {
    const refArticleUsage: ArticleUsageVO = this._articleLocation.referenceArticleUsage;
    const usageId: string = this.compositeArticleUtils.getBaseCompositePartArticleUsageIdByArticleUsageId(refArticleUsage.id);
    const refArticleContainer = this.containerService.findArticleContainerInGlobal(usageId);
    if (!this.findRangeOnLineArticle(refArticleContainer)) {
      await this.processFailure(`Could not load accepting area`);
      const errorMessage = this.localeResourceManager.getValueByKey('addArticleToRelativeLocationProcessor.message.range_on_line_connection_not_found');
      throw new AddArticleError(errorMessage)
    }
    this._neighborArticleContainer = refArticleContainer;
  }

  private findRangeOnLineArticle(refArticleContainer: ArticleContainer): boolean {
    if (!refArticleContainer) {
      return false;
    }
    const parentAcceptingAreaIds: MetaElementUsageIdentityVO[] =
      ArticleUsageUtils.getBackwardConnectedConnectionUsageIds(refArticleContainer.articleUsage);
    if (parentAcceptingAreaIds.length > 0) {
      for (const identity of parentAcceptingAreaIds) {
        const metaElement: MetaElementVO = this.projectMetaElementService.getMetaElementByUsage(identity.articleUsageId, identity.metaElementName);
        if (ConnectionInfoUtils.isDefaultRangeOnLineConnection(metaElement)) {
          this._articleContainer = this.containerService.findArticleContainerInGlobal(identity.articleUsageId);
          this._acceptingConnectionArea = metaElement as ConnectionAreaVO;
          return true;
        }
      }
    }
    return false;
  }

  protected async insertArticle(): Promise<InsertedArticleData> {
    const givingCubes: Connection[] = this.findAllGivingCubesForConnect();
    if (givingCubes && givingCubes.length > 0 && givingCubes[0].compositionRectangle) {
      const articleWidth: number = this.calculateArticleWidth(givingCubes);
      const neighborLine: AdvancedLine = this.calculateNeighborArticleLine(this._neighborArticleContainer);
      const articleLine: AdvancedLine = this.calculateArticleLine(neighborLine, articleWidth, this._articleLocation.articleOffset,
        this._articleLocation.relativePosition);
      const rotationAngle: number = this.getRotateAngleOfBaseContainer(this._articlesWrapper.baseArticleContainer,
        this.getRotationAngleByReferenceArticle());
      if (rotationAngle === 180) {
        articleLine.reverseOverlapProperties();
      }
      const busbarContainers: ArticleContainer[] = this.replaceBusbarService.getBusbarsToRecreateByInsertionPoint(
        articleLine.start,
        this._insertArticleHelper);
      // move articles
      const moveMap: Dictionary<string, Vector3> = this._insertArticleHelper.calculateMoveOffsetByArticleLine(articleLine, busbarContainers.map((busbar) => busbar.articleGUID));
      const labelingContainers: ArticleContainer[] = this.replaceLabelingUtils.getLabelStripsToRecreateByMoveMap(
        articleLine.start,
        moveMap,
        this._insertArticleHelper);

      if (moveMap && !CompositeArticleUtils.isCompositePartArticleExists(labelingContainers) && !CompositeArticleUtils.isCompositePartArticleExists(busbarContainers)) {
        const detachOperation: IOperation = await this.replaceLabelingHelper.detachLabelStrips(labelingContainers);
        if (detachOperation) {
          this._additionalUndoOperations.push(detachOperation);
        }

        await this.replaceBusbarHelper.detachBusbarContainers(busbarContainers, moveMap, this._insertArticleHelper);
        const busbarsEndOverhangChildrenMoveMap: Dictionary<string, Vector3> = this.replaceBusbarService.getEndOverhangChildrenMoveMap(this.replaceBusbarHelper.pendingBusbars, moveMap, this._insertArticleHelper);

        InsertArticleUtils.mergeMoveMap(moveMap, busbarsEndOverhangChildrenMoveMap);


        this.insertArticleUtils.moveArticlesByMap(moveMap);

        const acceptingConnection: Connection = this._insertArticleHelper.createConnectionOnLine(articleLine, this._acceptingConnectionArea);

        this._insertProcessor.process(
          this._articlesWrapper.articleContainers,
          this._articlesWrapper.baseArticleContainers,
          articleLine,
          acceptingConnection,
          givingCubes,
          rotationAngle,
          undefined,
          true
        );
        acceptingConnection.articleContainer.disposeConnectionByName(acceptingConnection.id);
        const createdContainerGUIDs: string[] = ArticleContainerUtils.extractArticleGUIDs(this._articlesWrapper.articleContainers);
        const data: any = {
          baseArticleGUID: this._articlesWrapper.baseArticleContainer.articleGUID,
          insertedArticleGUIDs: createdContainerGUIDs,
          moveArticlesMap: moveMap,
          articleLocation: this._articleLocation,
          createUndoOperation: true,
          undoOperations: this._additionalUndoOperations,
          isAccessoriesAdded: false,
        };
        return data
      } else {
        const errorMessage: string = this.localeResourceManager.getValueByKey(
          'articleLocationProcessor.connect.message.error_not_enough_free_space');

        await super.processFailure(`Not enough space.`);
        throw new NotEnoughSpaceError(errorMessage);
      }
    } else {
      const errorMessage: string = this.localeResourceManager.getValueByKey('articleLocationProcessor.message.error_unknown_connection',
        {'0': this._articlesWrapper.article.articleNo, '1': this._givingConnectionAreas[0].name});

      await super.processFailure(`Unknown connection for existing cubes.`);
      throw new AddArticleError(errorMessage);
    }
  }

  private getRotationAngleByReferenceArticle(): number {
    let rotationAngle: number = this._articleLocation.rotationAngle;
    let rotationDelta: number = NaN;
    if (this._articleLocation.applyRotationDelta && this._articleLocation.referenceArticleUsage) {
      let articleUsage = this._articleLocation.referenceArticleUsage;
      if (!this._articleLocation.referenceArticleUsage.articleId) {
        const articleUsageID = this.compositeArticleUtils.getBaseCompositePartArticleUsageIdByArticleUsageId(this._articleLocation.referenceArticleUsage.id);
        articleUsage = this.projectModel.getArticleUsage(articleUsageID);
      }

      rotationDelta = this.articleAccessoryManager.getRotationDelta(articleUsage.articleId, this._article.identity);
    }
    if (NumberUtils.isNumber(rotationDelta)) {
      if (NumberUtils.isNotNumber(rotationAngle)) {
        rotationAngle = rotationDelta;
      } else {
        rotationAngle += rotationDelta;
      }
    }
    return rotationAngle;
  }

  private calculateArticleLine(neighborLine: AdvancedLine, articleLength: number, articleOffset: number,
                               relativePosition: number): AdvancedLine {
    let startPoint: Vector3;
    let endPoint: Vector3;
    const neighborLength: number = neighborLine.length;
    const moveArticleOffset: number = NumberUtils.isNotNumber(articleOffset) ? 0 : articleOffset;
    const dummyLine: AdvancedLine = AdvancedLine.createInstanceByArticle(this._article);

    if (relativePosition === RelativePositionReferenceArticleLocationVO.RELATIVE_POSITION_LEFT_OF) {
      this._insertArticleHelper.getLeftArticleContainerByPoint(neighborLine.start);
      const overlapDistance: number = AdvancedLine.calculateMaxOverlapLength(dummyLine, neighborLine);

      startPoint = MathUtils.getInterpolated(
        neighborLine.start,
        neighborLine.end,
        (articleLength + neighborLength - overlapDistance) / neighborLength
      );
      endPoint = MathUtils.getInterpolated(neighborLine.start, neighborLine.end, (neighborLength - overlapDistance) / neighborLength);

      const distanceFromStart: number = this._insertArticleHelper.baseLine.start.distanceTo(endPoint);
      if (
        MathUtils.compare(distanceFromStart, articleLength - overlapDistance) === -1 ||
        this._insertArticleHelper.getConnectedArticlesFromLine(AdvancedLine.createInstanceByArticle(this._article, startPoint, endPoint)).length > 0
      ) {
        startPoint = neighborLine.start;
        endPoint = MathUtils.getInterpolated(neighborLine.end, neighborLine.start, (articleLength - overlapDistance) / neighborLength);
      }
    } else {
      const overlapDistance: number = AdvancedLine.calculateMaxOverlapLength(neighborLine, dummyLine);
      startPoint = MathUtils.getInterpolated(
        neighborLine.end,
        neighborLine.start,
        (neighborLength - overlapDistance + moveArticleOffset) / neighborLength
      );
      endPoint = MathUtils.getInterpolated(
        neighborLine.end,
        neighborLine.start,
        (articleLength + neighborLength - overlapDistance + moveArticleOffset) / neighborLength
      );
    }

    return AdvancedLine.createInstanceByArticle(this._article, startPoint, endPoint);
  }

  private calculateNeighborArticleLine(neighborArticle: ArticleContainer): AdvancedLine {
    let neighborLine: AdvancedLine;
    const compositeArticleUsageId: string = CompositeArticleInfoUtils.getCompositeArticleUsageId(neighborArticle);
    if (compositeArticleUsageId == null) {
      neighborLine = this._insertArticleHelper.getArticleLine(neighborArticle);
    } else {
      const baseContainers: ArticleContainer[] = this.containerService.getBaseCompositePartArticles(neighborArticle);
      neighborLine = this._insertArticleHelper.calculateCommonArticlesLine(baseContainers);
    }
    return neighborLine;
  }
}
